/* Copyright (c) 2020-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { LoggingLevel, LoggerSettings } from '../../libraries/crimson/src/lib/logging/logging-types';

export const DefaultCacheWarRetryAttempts = 3;

export enum SwRuntimeStoragePrefixes {
    Gfn = 'geforcenow',
    Nvapp = 'nvidiaapplication'
}

export enum SwNotificationTypes {
    FetchSuccess = 'SW_FETCH_SUCCESS',
    RuntimeCacheUpdated = 'SW_RUNTIME_CACHE_UPDATED'
}

export interface SwRuntimeCacheUpdatedNotification {
    name: string;
    url: string;
    data: any;
    noDataUpdate?: boolean;
}

export interface SwFetchSucceededParams {
    responseStatusCode: number;
    responseBody?: string;
    requestUrl: string;
    requestId: string;
    responseReceivedTimeMs: number;
    cacheName?: string;
}

export interface SwRuntimeCacheUpdatedPayload {
    cacheName: string;
    requestUrl: string;
    noDataUpdate?: boolean;
}

// add more notification types as unions
export type SwNotificationPayload = SwRuntimeCacheUpdatedPayload | SwFetchSucceededParams;

export interface SwNotification {
    type: SwNotificationTypes;
    payload: SwNotificationPayload;
}

export interface SwCacheExpiry {
    maxEntries?: number;
    maxAgeSeconds?: number;
    purgeOnQuotaError?: boolean;
}

export interface SwCacheManifestEntry {
    name: string;
    cacheName: string;
    routePattern: RegExp;
    expiry?: SwCacheExpiry;
    networkTimeoutSeconds?: number;
    maxRetentionTime?: number;
    updateCacheKey?: boolean;
    flushCacheOnResponseCodes?: number[];
}

export interface SwCacheManifestPolicy {
    [e: string]: SwCacheManifestEntry;
}

export interface SwCacheManifest {
    staleWhileRevalidate: SwCacheManifestPolicy;
    networkFirst: SwCacheManifestPolicy;
    networkOnly: SwCacheManifestPolicy;
    redirectSanitizer?: SwCacheManifestPolicy;
}

export enum CommonHeaderNames {
    ETag = 'etag',
    SwCacheBypass = 'x-sw-cachebypass',
    XRequestId = 'x-request-id',
    SwCacheKeyAppendString = 'sw-cache-key-append-string',
    SwCache = 'sw-cache',
    SwNotifyFetch = 'sw-notify-fetch',
    SwNoDataUpdate = 'sw-no-data-update'
}

export enum SwMessageType {
    SkipWaiting = 'SKIP_WAITING',
    GameLaunched = 'GAME_LAUNCHED',
    GameExit = 'GAME_EXIT',
    CloseStreamerSession = 'CLOSE_STREAM_SESSION',
    PushNotification = 'BROWSER_PUSH_NOTIFICATION',
    IpcPushNotification = 'IPC_PUSH_NOTIFICATION',
    PushNotificationTelemetry = 'PUSH_NOTIFICATION_TELEMETRY',
    UiPushNotification = 'BROWSER_VISIBLE_PUSH_NOTIFICATION',
    PushNotificationTracing = 'PUSH_NOTIFICATION_TRACING',
    PushNotificationReceived = 'PUSH_NOTIFICATION_RECEIVED',
    SharedStorageUpdate = 'SHARED_STORAGE_UPDATE',
    LoggerNotification = 'LOGGER_NOTIFICATION',
    GetClientVersion = 'GET_CLIENT_VERSION',
    SetLoggerSettings = 'SET_LOGGING_SETTINGS',
    GetClientVersionWaitingSW = 'GET_CLIENT_VERSION_FROM_WAITING_SW'
}
export interface SwActivatedEvent {
    firstInstallation: boolean;
    ts: number;
}

export interface SwUpdateResult {
    updateAvailable: boolean;
    error: Error;
}

export interface SwUpdateEvent {
    state: SwUpdateState;
    firstInstallation?: boolean;
    ts: number;
}

export interface PostMessageData {
    type: SwMessageType;
    content?: string;
}

export enum SwUpdateState {
    INSTALLING = 'installing',
    INSTALLED = 'installed',
    ACTIVATING = 'activating',
    ACTIVATED = 'activated',
    REDUNDANT = 'redundant'
}

export interface TelemetryEventData {
    type: PushNotificationTelemetryTypes;
    data: any;
}

export enum PushNotificationTelemetryTypes {
    PushNotificationReceived = 'PushNotificationReceived',
    PushNotificationError = 'PushNotificationError'
}

export enum NotificationChildSpan {
    FirebaseInitialisation = 'FirebaseInitialisation',
    FirebaseToken = 'FirebaseToken'
}

export interface TracingEventData {
    spanName: NotificationChildSpan;
    start: boolean;
    error?: boolean;
}

export let loggerSettingsGlobal: LoggerSettings = {
    remoteLoggingEnabled: false,
    consoleLoggingEnabled: true,
    loggingLevel: LoggingLevel.Event,
    remoteLogFunction: null
};
